import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import analytics from "@/lib/analytics";
import { Sparkles, ArrowRight, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PublicPreviewWidget — anonymous, no-signup "Regulatory Snapshot Preview".
 * Uses the deterministic rule engine only (no Claude, no PHI stored).
 * Rate-limited per IP on the backend.
 */
export default function PublicPreviewWidget() {
  const [deviceName, setDeviceName] = useState("");
  const [intendedUse, setIntendedUse] = useState("");
  const [contact, setContact] = useState("none");
  const [software, setSoftware] = useState(false);
  const [aiMl, setAiMl] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/preview/classification`, {
        device_name: deviceName.trim(),
        intended_use: intendedUse.trim(),
        patient_contact: contact,
        software_involved: software,
        ai_ml_involved: aiMl,
      });
      setResult(data);
      analytics.track("public_preview_submitted", {
        patient_contact: contact,
        software_involved: software,
        ai_ml_involved: aiMl,
        fda_class: data.markets?.[0]?.class,
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError("You've reached the free preview limit. Please try again in a minute, or create a free account to continue.");
      } else if (status === 400 && typeof detail === "string") {
        setError(detail);
      } else if (status === 422) {
        setError("Please fill out device name (2+ chars) and intended use (10+ chars).");
      } else {
        setError("We couldn't run the preview right now. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <section
      id="preview"
      className="max-w-7xl mx-auto px-6 py-24"
      data-testid="public-preview-section"
    >
      <div className="max-w-2xl mb-10">
        <div className="vq-overline mb-3 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-[#0055FF]" />
          Try it free · No signup
        </div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
          Get a regulatory snapshot in under a minute.
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed">
          Describe your device idea. VitaQMS returns a rule-based classification preview across
          FDA, EU MDR, and CDSCO — instantly, no account needed. Every result is orientation only;
          your qualified regulatory team still owns the final call.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <form
          onSubmit={submit}
          className="lg:col-span-5 vq-card p-6 shadow-xl shadow-slate-900/5"
          data-testid="public-preview-form"
        >
          <div className="mb-4">
            <Label htmlFor="pp-device-name" className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Device name
            </Label>
            <Input
              id="pp-device-name"
              data-testid="public-preview-device-name"
              placeholder="e.g. NeuroMonitor v1"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              maxLength={120}
              required
              className="mt-1"
            />
          </div>

          <div className="mb-4">
            <Label htmlFor="pp-intended-use" className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Intended clinical use
            </Label>
            <Textarea
              id="pp-intended-use"
              data-testid="public-preview-intended-use"
              placeholder="Describe the target patients, clinical setting, and what the device does. Do NOT include any patient identifiers."
              value={intendedUse}
              onChange={(e) => setIntendedUse(e.target.value)}
              rows={4}
              maxLength={800}
              required
              className="mt-1 resize-none"
            />
            <div className="text-[10px] text-slate-400 mt-1 font-mono text-right">
              {intendedUse.length}/800
            </div>
          </div>

          <div className="mb-4">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Patient contact
            </Label>
            <Select value={contact} onValueChange={setContact}>
              <SelectTrigger
                className="mt-1"
                data-testid="public-preview-contact"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (external / non-contact)</SelectItem>
                <SelectItem value="surface">Skin or mucous membrane</SelectItem>
                <SelectItem value="invasive">Invasive (short-term)</SelectItem>
                <SelectItem value="implant">Implantable / long-term</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 mb-6">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox
                checked={software}
                onCheckedChange={setSoftware}
                data-testid="public-preview-software"
              />
              <span>Software / SaMD component</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox
                checked={aiMl}
                onCheckedChange={setAiMl}
                data-testid="public-preview-aiml"
              />
              <span>Uses AI / ML models</span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={loading || deviceName.trim().length < 2 || intendedUse.trim().length < 10}
            className="w-full bg-slate-900 hover:bg-[#0055FF] text-white h-11 font-semibold"
            data-testid="public-preview-submit"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</>
            ) : (
              <>Preview classification <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>

          <p className="mt-3 text-[10px] text-slate-400 leading-relaxed font-mono">
            Rule-based only · No Claude tokens spent · Nothing is stored.
          </p>
        </form>

        {/* Result / placeholder */}
        <div className="lg:col-span-7">
          {!result && !error && (
            <div
              className="vq-card p-8 h-full flex flex-col items-center justify-center text-center border-dashed"
              data-testid="public-preview-placeholder"
            >
              <ShieldCheck className="w-10 h-10 text-slate-300 mb-4" />
              <div className="text-slate-500 font-mono text-xs uppercase tracking-wider mb-2">
                Your snapshot appears here
              </div>
              <p className="text-slate-400 text-sm max-w-sm">
                Fill in the form and hit <em>Preview classification</em> to see FDA, EU MDR, and CDSCO candidate classes.
              </p>
            </div>
          )}

          {error && (
            <div
              className="vq-card p-6 border-l-4 border-l-amber-500 bg-amber-50/50"
              data-testid="public-preview-error"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-800">{error}</div>
              </div>
              <Button
                variant="outline"
                onClick={reset}
                className="mt-4 h-9 text-xs"
                data-testid="public-preview-retry"
              >
                Try again
              </Button>
            </div>
          )}

          {result && (
            <div className="vq-card p-6 shadow-xl shadow-slate-900/5" data-testid="public-preview-result">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="vq-overline mb-1">Snapshot preview</div>
                  <div className="font-display font-bold text-xl text-slate-900" data-testid="public-preview-result-name">
                    {result.device_name}
                  </div>
                </div>
                <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">
                  Rule-based · Illustrative
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                {result.markets.map((m) => (
                  <div
                    key={m.code}
                    className="border border-slate-200 rounded-md p-3 hover:border-[#0055FF] transition-colors"
                    data-testid={`public-preview-market-${m.code.toLowerCase()}`}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                      {m.label}
                    </div>
                    <div className="font-display font-bold text-lg text-slate-900 mt-0.5 leading-tight">
                      {m.class}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{m.rule}</div>
                    <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">
                      {m.rationale}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-l-4 border-l-[#0055FF] bg-slate-50 p-4 mb-4">
                <div className="text-xs font-mono uppercase text-slate-500 mb-2">Next steps</div>
                <ul className="text-sm text-slate-700 space-y-1.5">
                  {result.next_steps.map((s) => (
                    <li key={s} className="flex gap-2">
                      <span className="text-[#0055FF] flex-shrink-0">•</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <Link to="/register" data-testid="public-preview-cta-register" className="flex-1">
                  <Button className="w-full bg-slate-900 hover:bg-[#0055FF] text-white h-11 font-semibold">
                    Continue in VitaQMS <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={reset}
                  className="h-11"
                  data-testid="public-preview-reset"
                >
                  New preview
                </Button>
              </div>

              <p className="mt-4 text-[10px] text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
                <b>{result.safety_note}</b> {result.disclaimer}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
